/**
 * PreloadManager — Central asset preloading service
 *
 * Images: Uses <link rel="preload"> to warm the browser's HTTP cache.
 * This guarantees that when an <img> element later requests the same URL,
 * it gets a cache hit — even on Safari/iOS which partitions the cache
 * between programmatic Image() objects and DOM elements.
 * Image preloading is fire-and-forget (no completion tracking).
 *
 * Videos: Fetched as blobs and stored in memory via the onVideoBlob handler,
 * since videos need to be served from blob URLs to avoid streaming delays.
 * Video preloading goes through a queue with concurrency control.
 *
 * Both use a shared "loaded" set to avoid duplicate requests.
 */

import { ImageSize, resolveImageUrl, resolveVideoUrl } from "./preloadConfig";
import {StrapiAsset} from "@/types/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PreloadPriority = "critical" | "high" | "low";

interface VideoQueueItem {
    url: string;
    priority: number;
    resolvers: Array<() => void>;
}

export interface BatchHandle {
    promise: Promise<void>;
    urls: Set<string>;
}

// ---------------------------------------------------------------------------
// Priority ordering (lower = higher priority)
// ---------------------------------------------------------------------------

const PRIORITY_ORDER: Record<PreloadPriority, number> = {
    critical: 0,
    high: 1,
    low: 2,
};

// ---------------------------------------------------------------------------
// Concurrency defaults
// ---------------------------------------------------------------------------

const MAX_CONCURRENT_VIDEOS = 1;

// ---------------------------------------------------------------------------
// Preload link management
// ---------------------------------------------------------------------------

/** Map of URL → <link> element, so we can clean them up later */
const preloadLinks = new Map<string, HTMLLinkElement>();

function addPreloadLink(url: string, fetchpriority: "high" | "low" | "auto" = "auto"): void {
    if (preloadLinks.has(url)) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    link.setAttribute("fetchpriority", fetchpriority);
    document.head.appendChild(link);
    preloadLinks.set(url, link);
}

function removePreloadLink(url: string): void {
    const link = preloadLinks.get(url);
    if (link) {
        link.remove();
        preloadLinks.delete(url);
    }
}

function removeAllPreloadLinks(): void {
    for (const link of preloadLinks.values()) {
        link.remove();
    }
    preloadLinks.clear();
}

// ---------------------------------------------------------------------------
// PreloadManager
// ---------------------------------------------------------------------------

export class PreloadManager {
    /** URLs that have been preloaded (images) or fetched (videos) */
    private loaded = new Set<string>();

    /** Video queue */
    private videoQueue: VideoQueueItem[] = [];
    private videoInFlight: Map<string, VideoQueueItem> = new Map();
    private activeVideos = 0;

    /** Callback for video blob caching — wired up by usePreloadStore */
    private onVideoBlob: ((originalUrl: string, blobUrl: string) => void) | null = null;

    // -----------------------------------------------------------------------
    // Configuration
    // -----------------------------------------------------------------------

    setVideoBlobHandler(handler: (originalUrl: string, blobUrl: string) => void) {
        this.onVideoBlob = handler;
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    /**
     * Preload a batch of Strapi images via <link rel="preload">.
     * Fire-and-forget — resolves immediately. The browser handles
     * fetching and caching; the same URL in an <img> tag will hit cache.
     */
    enqueueImages(
        images: StrapiAsset[],
        size: ImageSize,
        priority: PreloadPriority,
    ): void {
        const fetchpriority = priority === "low" ? "low" : "high";

        for (const image of images) {
            try {
                const url = resolveImageUrl(image, size);
                if (this.loaded.has(url)) continue;
                this.loaded.add(url);
                addPreloadLink(url, fetchpriority);
            } catch {
                // Skip malformed image data silently
            }
        }
    }

    /**
     * Enqueue a video for preloading.
     * Videos are fetched as blobs and stored via the onVideoBlob handler.
     */
    enqueueVideo(media: StrapiAsset, priority: PreloadPriority): BatchHandle {
        try {
            const url = resolveVideoUrl(media);
            const promise = this.enqueueVideoUrl(url, priority);

            return {
                promise: promise.then(() => undefined),
                urls: new Set([url]),
            };
        } catch {
            return {
                promise: Promise.resolve(),
                urls: new Set(),
            };
        }
    }

    /**
     * Remove URLs from the loaded set and clean up preload links.
     * Call during phase transitions for assets no longer needed.
     * For videos, also call usePreloadStore.revokeVideo() to free the blob.
     */
    clearLoaded(urls: Iterable<string>) {
        for (const url of urls) {
            this.loaded.delete(url);
            removePreloadLink(url);
        }
    }

    /**
     * Remove all image preload links from <head> and clear the loaded set for images.
     * Call during phase transitions to keep memory lean.
     * Already-rendered images remain in the browser's regular HTTP cache.
     */
    clearAllImagePreloads() {
        removeAllPreloadLinks();
        // Only clear image URLs from loaded set — keep video URLs
        // so they don't get re-fetched
        for (const url of this.loaded) {
            if (!this.videoInFlight.has(url)) {
                // Simple heuristic: video URLs contain video extensions
                if (!/\.(mp4|webm|ogg)(\?|$)/i.test(url)) {
                    this.loaded.delete(url);
                }
            }
        }
    }

    // -----------------------------------------------------------------------
    // Video queue internals
    // -----------------------------------------------------------------------

    private enqueueVideoUrl(url: string, priority: PreloadPriority): Promise<void> {
        if (this.loaded.has(url)) return Promise.resolve();

        // Already in-flight
        const existing = this.videoInFlight.get(url);
        if (existing) {
            return new Promise((resolve) => {
                existing.resolvers.push(resolve);
            });
        }

        // Already queued
        const queued = this.videoQueue.find((item) => item.url === url);
        if (queued) {
            return new Promise((resolve) => {
                queued.resolvers.push(resolve);
            });
        }

        // New item
        return new Promise<void>((resolve) => {
            this.videoQueue.push({
                url,
                priority: PRIORITY_ORDER[priority],
                resolvers: [resolve],
            });
            this.processVideoQueue();
        });
    }

    private resolveAll(item: VideoQueueItem) {
        for (const resolve of item.resolvers) {
            resolve();
        }
        item.resolvers.length = 0;
    }

    private processVideoQueue() {
        this.videoQueue.sort((a, b) => a.priority - b.priority);

        while (this.videoQueue.length > 0 && this.activeVideos < MAX_CONCURRENT_VIDEOS) {
            const next = this.videoQueue.shift()!;
            this.startVideoFetch(next);
        }
    }

    private async startVideoFetch(item: VideoQueueItem) {
        const { url } = item;

        this.videoInFlight.set(url, item);
        this.activeVideos++;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Failed to fetch video: ${url} (${response.status})`);

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            if (this.onVideoBlob) {
                this.onVideoBlob(url, blobUrl);
            }

            this.loaded.add(url);
        } catch (err) {
            console.warn(`Failed to preload video: ${url}`, err);
        } finally {
            this.resolveAll(item);
            this.videoInFlight.delete(url);
            this.activeVideos--;
            this.processVideoQueue();
        }
    }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

export const preloadManager = new PreloadManager();